import {FC, ReactNode} from 'react'

import {getAboutUsContent} from '@/content/aboutUs'
import {useTranslation} from '@/i18n'
import {HOVER_TRANSITION, SLIDER_TRANSITION} from '@/theme/cssTransitions'
import {theme} from '@/theme/theme'
import {MOBILE_SCREENSIZE} from '@/utils/styles/breakpoints'

import Carousel from '../common/carousel/Carousel'
import {Text, TextVariant} from '../common/content/Text'

type StoryItemProps = {
  date: string
  description: string
}

const StoryItem: FC<React.PropsWithChildren<StoryItemProps>> = ({date, description}) => {
  return (
    <>
      <div className="item">
        <div className="content">
          <Text variant={TextVariant.h4} element="h3" color={theme.palette.text.primary}>
            {date}
          </Text>
          <div className="description">
            <Text variant={TextVariant.text20} color={theme.palette.text.light}>
              {description}
            </Text>
          </div>
        </div>
      </div>

      <style jsx>{`
        .content {
          text-align: center;
        }
        .description {
          max-width: 600px;
          margin: ${theme.spacing[2.5]} auto 0;
        }
        @media ${MOBILE_SCREENSIZE} {
          .content {
            text-align: left;
            padding-left: ${theme.spacing[5]};
          }
          .item {
            position: relative;
          }
          .item:not(:last-child) {
            padding-bottom: ${theme.spacing[4]};
          }
          .item:not(:last-child)::before {
            content: '';
            position: absolute;
            top: 8px;
            left: 7px;
            width: 3px;
            height: 100%;
            background-image: linear-gradient(
              ${theme.palette.gray[300]} 50%,
              ${theme.palette.background.default} 50%
            );
            background-size: 3px 28px;
            background-repeat: repeat-y;
          }
          .item::after {
            content: '';
            position: absolute;
            top: 8px;
            width: 16px;
            height: 16px;
            flex: 0 0 16px;
            background-color: ${theme.palette.text.primary};
            border-radius: 50%;
          }
          .description {
            margin-top: 0;
          }
        }
      `}</style>
    </>
  )
}

export const StoryTimeline: FC<React.PropsWithChildren<unknown>> = () => {
  const {t} = useTranslation()
  const {title, timeline} = getAboutUsContent().story

  const settings = {
    infinite: true,
    speed: SLIDER_TRANSITION,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 5000,
    arrows: false,
    dots: true,
    appendDots: (dots: ReactNode) => <ul>{dots}</ul>,
    customPaging: (i: number) => (
      <button type="button" aria-label={t(timeline[i].date)}>
        <span className="dot" />
      </button>
    ),
  }

  return (
    <>
      <Text variant={TextVariant.h2} align="center">
        {t(title)}
      </Text>
      <div className="story">
        <div className="desktop">
          <Carousel settings={settings}>
            {timeline.map(({date, description}) => (
              <StoryItem key={date} date={t(date)} description={t(description)} />
            ))}
          </Carousel>
        </div>
        <div className="mobile">
          {timeline.map(({date, description}) => (
            <StoryItem key={date} date={t(date)} description={t(description)} />
          ))}
        </div>
      </div>

      <style jsx>{`
        .story {
          margin-top: ${theme.spacing[8]};
        }
        .mobile {
          display: none;
        }
        @media ${MOBILE_SCREENSIZE} {
          .story {
            margin-top: ${theme.spacing[6]};
          }
          .desktop {
            display: none;
          }
          .mobile {
            display: block;
          }
        }
      `}</style>

      {/* Please, use global styles only if you need to override CSS from external libraries */}
      <style jsx global>{`
        .story .slick-slider {
          display: flex;
          flex-direction: column-reverse;
          max-width: 100%;
        }
        .story .slick-list {
          margin-top: ${theme.spacing[6]};
        }
        .story .slick-dots {
          display: flex;
          margin-top: 0;
        }
        .story .slick-dots li {
          display: flex;
          align-items: center;
          cursor: default;
        }
        .story .slick-dots li:not(:last-child) {
          flex: 1;
        }
        .story .slick-dots li:not(:last-child)::before {
          content: '';
          position: absolute;
          width: 100%;
          height: 3px;
          left: 4px;
          background-image: linear-gradient(
            to right,
            ${theme.palette.gray[300]} 50%,
            ${theme.palette.background.default} 50%
          );
          background-size: 28px 3px;
          background-repeat: repeat-x;
          background-position: center;
        }
        .story .slick-dots li button {
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0;
        }
        .story .slick-dots li button:before {
          content: none;
        }
        .story .slick-dots li .dot {
          position: relative;
          width: 16px;
          height: 16px;
          flex: 0 0 16px;
          background-color: ${theme.palette.gray[300]};
          border-radius: 50%;
        }
        .story .slick-dots li.slick-active .dot {
          width: 32px;
          height: 32px;
          flex: 0 0 32px;
          background-color: ${theme.palette.primary.main};
          transition: background-color ${HOVER_TRANSITION}ms ease-in-out;
        }
        @media ${MOBILE_SCREENSIZE} {
          .story .slick-list {
            margin-top: ${theme.spacing[5]};
          }
        }
      `}</style>
    </>
  )
}
